import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Layout, X } from 'lucide-react';
import { getPaneGroups } from './availablePanes';

/**
 * Layout Customizer Component
 * Allows users to select which panes to display
 */
const LayoutCustomizer = ({ 
  isOpen, 
  onClose, 
  availablePanes, 
  currentLayout, 
  onLayoutSave 
}) => {
  const { language } = useLanguage();
  const [tempLayout, setTempLayout] = useState(currentLayout);

  const handleTogglePane = (paneId) => {
    setTempLayout(prev => 
      prev.includes(paneId) 
        ? prev.filter(id => id !== paneId)
        : [...prev, paneId]
    );
  };

  const handleSave = () => {
    onLayoutSave(tempLayout);
    onClose();
  };

  const handleReset = () => {
    const defaultLayout = ['bible', 'reading-plans'];
    setTempLayout(defaultLayout);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layout className="w-5 h-5" />
            {language === 'es' ? 'Personalizar Diseño' : 'Customize Layout'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es' 
              ? 'Selecciona las herramientas que deseas mostrar en tu panel de estudio'
              : 'Select the tools you want to display in your study dashboard'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Selected count */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>{tempLayout.length}</strong> {language === 'es' ? 'herramientas seleccionadas' : 'tools selected'}
            </p>
          </div>

          {/* Pane selection grouped by category */}
          {getPaneGroups(language).map((group) => (
            <div key={group.key} data-testid={`customizer-group-${group.key}`}>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-primary">
                {group.label}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {group.panes.map((pane) => (
                  <div 
                    key={pane.id}
                    className="flex items-center space-x-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    <Checkbox
                      id={pane.id}
                      checked={tempLayout.includes(pane.id)}
                      onCheckedChange={() => handleTogglePane(pane.id)}
                    />
                    <Label 
                      htmlFor={pane.id} 
                      className="flex-1 cursor-pointer text-sm font-medium"
                    >
                      {pane.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t">
            <Button 
              onClick={handleSave} 
              className="flex-1"
              disabled={tempLayout.length === 0}
            >
              {language === 'es' ? 'Guardar Diseño' : 'Save Layout'}
            </Button>
            <Button 
              onClick={handleReset} 
              variant="outline"
              className="flex-1"
            >
              {language === 'es' ? 'Restablecer Predeterminado' : 'Reset to Default'}
            </Button>
            <Button 
              onClick={onClose} 
              variant="ghost"
            >
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LayoutCustomizer;
